import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router";

import { useRoutesContext } from "../Routes/RoutesContext";
import dispatchEvent from "../Utils/dispatchEvent";
import { useCurrentRoute } from "./useCurrentRoute";

import { setLocation, setStoredLocation } from "../../reducers/location";

const RouterLocationManager = props => {
  const dispatch = useDispatch();

  const { children } = props;

  const needsToEditProfile = useSelector(
    ({ viewer }) => (viewer || {}).needsToEditProfile
  );
  const needsToFillPostSignUpForm = useSelector(
    ({ viewer }) => (viewer || {}).needsToFillPostSignUpForm
  );
  const storedLocation = useSelector(({ location }) => location.storedLocation);
  const lastNonModalLocation = useSelector(
    ({ location }) => location.lastNonModalLocation
  );
  const { location, ...route } = useCurrentRoute();
  const { pathFor } = useRoutesContext();
  const configureAccountPath = pathFor("configureAccount");
  const postSignUpFormPath = pathFor("postSignUpForm");

  const willRedirectToPostSignUpForm =
    needsToFillPostSignUpForm && location.pathname !== postSignUpFormPath;

  // If the user needs to fill the post sign upform AND edit their profile, we first let
  // the user fill the post sign up form and then redirect to the configure account page,
  // avoiding redirect loops.
  const willRedirectToConfigureAccount =
    !needsToFillPostSignUpForm &&
    needsToEditProfile &&
    location.pathname !== configureAccountPath;

  const documentWillBeCached = useRef(false);

  useEffect(() => {
    const handleDocumentWillBeCached = () => {
      documentWillBeCached.current = true;
    };

    document.addEventListener("turbo:before-cache", handleDocumentWillBeCached);

    return () =>
      document.removeEventListener("turbo:before-cache", handleDocumentWillBeCached);
  }, []);

  useEffect(() => {
    const isModal = route?.isModal || false;

    // Restore the location the viewer came from before a reload, so modal routes keep
    // a non-modal location to return to.
    if (isModal && storedLocation && !lastNonModalLocation) {
      dispatch(setLocation({ ...storedLocation, isModal: false }));
    }

    // Store the current location with its associted route data to let the redux
    // action handler process the location accordingly, and avoid consumers
    // having to resolve the route later
    dispatch(setLocation({ ...location, ...route, isModal }));

    if (willRedirectToConfigureAccount || willRedirectToPostSignUpForm) {
      dispatch(setStoredLocation(location));
    }
  }, [
    route,
    storedLocation,
    lastNonModalLocation,
    willRedirectToConfigureAccount,
    willRedirectToPostSignUpForm,
    dispatch,
    location
  ]);

  // Since the layout could have changed because of the full screen update,
  // we manually trigger a window resize that components may listen to calculate
  // their own layout.
  //
  // This could be ported as a redux window size value in the store to avoid
  // binding the window resize event in every component that need it.
  //
  useLayoutEffect(() => {
    if (documentWillBeCached.current) return;

    dispatchEvent(window, "resize");
  }, [location]);

  if (willRedirectToConfigureAccount) {
    return <Navigate to={configureAccountPath} />;
  }

  if (willRedirectToPostSignUpForm) {
    return <Navigate to={postSignUpFormPath} />;
  }

  return children;
};

export default RouterLocationManager;
